import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CreditCard } from 'lucide-react';
import * as crmBackend from '../services/crmBackendService';

/**
 * Invoice Detail Dialog — extracted verbatim from Dashboard.jsx (formerly
 * inline lines 1528-1614) so both Dashboard.jsx and ContactDetail.jsx
 * (Phase 2B customer history timeline) can mount it. Markup and handlers are
 * unchanged; only state ownership moved from Dashboard-local useState to
 * props so the caller drives it.
 *
 * Props:
 *   open, onOpenChange     — dialog visibility (was invoiceDetailOpen/setInvoiceDetailOpen)
 *   invoice                — the loaded invoice doc (was selectedInvoice)
 *   sending, onSend        — Send Invoice to Customer action (was sendingInvoice/handleSendInvoice)
 *   onRecordPayment(inv)   — opens the caller's own "Record Payment" dialog (was handleOpenPayment)
 *   onProcessPayment(inv)  — opens the caller's own ProcessPaymentDialog / charge-card flow (was setChargeInvoice)
 *   getStatusColor         — status→className lookup (Dashboard-local function, passed through)
 */
export default function InvoiceDetailDialog({
  open,
  onOpenChange,
  invoice: selectedInvoice,
  sending: sendingInvoice,
  onSend: handleSendInvoice,
  onRecordPayment,
  onProcessPayment,
  getStatusColor,
}) {
  // Communications — same source ContactDetail.jsx/TechJobDetails.jsx use
  // (getContactHistory), filtered down to just this invoice, so "was this
  // actually sent to the customer" is answered right where staff look after
  // clicking Send Invoice to Customer, not only on a separate Contact page.
  const [communications, setCommunications] = useState([]);
  const [commsLoading, setCommsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !selectedInvoice?.customer || !selectedInvoice?.name) {
        setCommunications([]);
        return;
      }
      setCommsLoading(true);
      try {
        const result = await crmBackend.getContactHistory(selectedInvoice.customer, 'customer');
        const all = result?.data?.communications || [];
        const forThisInvoice = all.filter(
          (c) => c.reference_doctype === 'Sales Invoice' && c.reference_name === selectedInvoice.name
        );
        if (!cancelled) setCommunications(forThisInvoice);
      } catch {
        if (!cancelled) setCommunications([]);
      } finally {
        if (!cancelled) setCommsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // sendingInvoice included so a send-in-progress -> done transition
    // (owned by the parent's handleSendInvoice) triggers an immediate
    // refetch, showing the new Communication without closing/reopening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedInvoice?.customer, selectedInvoice?.name, sendingInvoice]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{selectedInvoice?.name} — {selectedInvoice?.customer_name || selectedInvoice?.customer}</DialogTitle>
        </DialogHeader>
        {selectedInvoice && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-gray-500">Status</p>
                <Badge className={getStatusColor(selectedInvoice.status)}>{selectedInvoice.status}</Badge>
              </div>
              <div>
                <p className="text-gray-500">Total</p>
                <p className="font-semibold text-base">{crmBackend.formatCurrency(selectedInvoice.grand_total)}</p>
              </div>
              {selectedInvoice.outstanding_amount > 0 && (
                <div>
                  <p className="text-gray-500">Outstanding</p>
                  <p className="font-semibold text-red-600">{crmBackend.formatCurrency(selectedInvoice.outstanding_amount)}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500">Date</p>
                <p className="font-medium">{selectedInvoice.posting_date}</p>
              </div>
              <div>
                <p className="text-gray-500">Due Date</p>
                <p className="font-medium">{selectedInvoice.due_date || '—'}</p>
              </div>
              {selectedInvoice.contact_mobile && (
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{selectedInvoice.contact_mobile}</p>
                </div>
              )}
              {selectedInvoice.contact_email && (
                <div className="col-span-2">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedInvoice.contact_email}</p>
                </div>
              )}
            </div>
            <div className="border-t pt-3 mt-1">
              <p className="text-gray-500 mb-1">Communications</p>
              {commsLoading && <p className="text-xs text-gray-400">Loading…</p>}
              {!commsLoading && communications.length === 0 && (
                <p className="text-xs text-gray-400">Not sent to the customer yet.</p>
              )}
              {!commsLoading && communications.map((c) => (
                <p key={c.name} className="text-xs text-gray-600">
                  ✉️ Sent {c.communication_date
                    ? new Date(c.communication_date).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                      })
                    : ''}
                </p>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              To collect payment or edit line items, open in{' '}
              <a
                href={`https://erp.aaairrigationservice.com/app/sales-invoice/${selectedInvoice.name}`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                ERPNext
              </a>
            </p>
          </div>
        )}
        <DialogFooter className="sticky bottom-0 z-10 -mx-6 -mb-6 flex-col gap-2 border-t bg-background px-6 py-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {/* Manual-only — emails the Pay Now link + Zelle block to the customer. */}
          {selectedInvoice?.docstatus === 1 && selectedInvoice?.outstanding_amount > 0 && (
            <Button
              variant="outline"
              onClick={handleSendInvoice}
              disabled={sendingInvoice}
              className="border-blue-600 text-blue-700 hover:bg-blue-50"
            >
              {sendingInvoice ? 'Sending…' : 'Send Invoice to Customer'}
            </Button>
          )}
          {selectedInvoice?.outstanding_amount > 0 && (
            <Button variant="outline"
              onClick={() => onRecordPayment(selectedInvoice)}>
              Record Payment
            </Button>
          )}
          {/* Manual-only — charges the customer's card live via Authorize.net.
              Only ever runs on this deliberate press. */}
          {selectedInvoice?.outstanding_amount > 0 && (
            <Button className="bg-green-600 hover:bg-green-700"
              onClick={() => { onOpenChange(false); onProcessPayment(selectedInvoice); }}>
              <CreditCard className="w-4 h-4 mr-2" />Process Payment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
